import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Message, IkigaiBuckets, DashboardUpdatePayload, ChatSummary } from '../types';
import { addMessage, getChatHistory, getChatSummaries, saveChatSummary, updateChatMessage } from '../services/dbService';
import { sendMessageToKai, parseKaiResponse, summarizeMessages, startNewChat, startTimeCapsuleChat, startOriginStoryChat, switchToStandardChat } from '../services/geminiService';
import { useKai } from '../contexts/KaiPresenceContext';

type ChatMode = 'standard' | 'time_capsule' | 'origin_story';

interface UseIkigaiChatProps {
    user: any;
    ikigaiData: IkigaiBuckets;
    userFacts: string[]; // Memory 2.0
    isDataHydrating: boolean;
    onDashboardUpdate: (updates: DashboardUpdatePayload) => void;
}

// --- HELPER: CALCULATE ARCHETYPE & PROGRESS (Logic Shared with Dashboard) ---
const calculateIkigaiStats = (data: IkigaiBuckets) => {
    // 1. Archetype Logic
    const s = {
        p: Math.min(1, data.passion.length / 5),
        m: Math.min(1, data.mission.length / 5),
        v: Math.min(1, data.vocation.length / 5),
        pr: Math.min(1, data.profession.length / 5)
    };
    const totalScore = s.p + s.m + s.v + s.pr;
    let archetype = "Calibrating";

    if (totalScore > 3.0) archetype = "Ikigai Aligned";
    else if (s.p > 0.6 && s.m > 0.6) archetype = "The Dreamer";
    else if (s.v > 0.6 && s.pr > 0.6) archetype = "The Provider";
    else if (s.pr > 0.6 && s.p > 0.6) archetype = "The Hustler";
    else if (totalScore > 0.8) archetype = "The Seeker";

    // 2. Progress Logic (Based on 8 core items)
    const totalItems = data.passion.length + data.mission.length + data.profession.length + data.vocation.length;
    const progress = Math.min(100, Math.round((totalItems / 8) * 100));

    return { archetype, progress };
};

export const useIkigaiChat = ({ user, ikigaiData, userFacts, isDataHydrating, onDashboardUpdate }: UseIkigaiChatProps) => {
    const { setMood: setKaiMood, state: kaiState } = useKai();

    const [chatMode, setChatMode] = useState<ChatMode>('standard');
    const [messages, setMessages] = useState<Message[]>([]);
    const [timeCapsuleMessages, setTimeCapsuleMessages] = useState<Message[]>([]);
    const [originStoryMessages, setOriginStoryMessages] = useState<Message[]>([]);
    const [summaries, setSummaries] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Ref to hold latest messages for hot-swap without re-render loop
    const messagesRef = useRef<Message[]>([]);
    // Ref to track last summarized count to prevent loops
    const lastSummarizedCount = useRef<number>(0);
    // Typing simulation
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => { messagesRef.current = messages; }, [messages]);

    // --- 1. Hydration (Load Chats & Summaries) ---
    useEffect(() => {
        const loadChats = async () => {
            if (!user || !user.uid || isDataHydrating) return;

            try {
                const pastSummaries = await getChatSummaries(user.uid, 5);
                const summaryTexts = pastSummaries.map(s => s.text);
                setSummaries(summaryTexts);

                if (summaryTexts.length > 0) {
                    console.log("🧠 [Memory System] Loaded past summaries:", summaryTexts.length);
                }

                const stdChats = await getChatHistory(user.uid, 'standard');
                const tcChats = await getChatHistory(user.uid, 'time_capsule');
                const osChats = await getChatHistory(user.uid, 'origin_story');

                setMessages(stdChats);
                setTimeCapsuleMessages(tcChats);
                setOriginStoryMessages(osChats);

                lastSummarizedCount.current = Math.floor(stdChats.length / 20) * 20;

                // Initialize Service Logic (Client Fallback)
                startNewChat(stdChats, kaiState.archetype, summaryTexts, userFacts, ikigaiData);
            } catch (e) {
                console.error("Chat load failed", e);
                startNewChat();
            }
        };
        loadChats();
    }, [user, isDataHydrating]);

    // --- 2. Persona Hot-Swap ---
    useEffect(() => {
        if (!isDataHydrating && chatMode === 'standard') {
            startNewChat(messagesRef.current, kaiState.archetype, summaries, userFacts, ikigaiData);
        }
    }, [kaiState.archetype, isDataHydrating, chatMode, userFacts, summaries, ikigaiData]);

    // --- 3. Auto-Summarization ---
    useEffect(() => {
        const checkSummary = async () => {
            const count = messages.length;
            if (count > 0 && count % 20 === 0 && count > lastSummarizedCount.current) {
                lastSummarizedCount.current = count;
                const recentSlice = messages.slice(-20);
                const summaryText = await summarizeMessages(recentSlice);

                if (summaryText && user?.uid) {
                    const newSummary: Omit<ChatSummary, 'id' | 'createdAt'> = {
                        conversationId: 'standard',
                        range: { start: count - 20, end: count },
                        text: summaryText
                    };
                    await saveChatSummary(user.uid, newSummary);
                    setSummaries(prev => [...prev, summaryText]);
                }
            }
        };

        if (chatMode === 'standard' && !isLoading) {
            checkSummary();
        }
    }, [messages, chatMode, isLoading, user]);

    // --- 4. Typing Simulator (UX Normalization) ---
    const simulateTyping = (fullText: string, messageId: string, setter: React.Dispatch<React.SetStateAction<Message[]>>, onComplete: () => void) => {
        let currentIndex = 0;
        const speed = fullText.length > 200 ? 5 : 15; // Faster for long messages

        const typeChar = () => {
            if (currentIndex < fullText.length) {
                const burst = Math.floor(Math.random() * 6) + 2; // Random chars per tick
                const chunk = fullText.slice(currentIndex, currentIndex + burst);
                currentIndex += chunk.length;

                setter(prev => prev.map(msg =>
                    msg.id === messageId ? { ...msg, text: msg.text + chunk } : msg
                ));

                typingTimeoutRef.current = setTimeout(typeChar, speed);
            } else {
                onComplete();
            }
        };
        typeChar();
    };

    // --- 5. Messaging Logic ---
    const handleSendMessage = useCallback(async (text: string) => {
        if (!user) return;

        let setter: React.Dispatch<React.SetStateAction<Message[]>>;
        let currentHistory: Message[] = [];
        let conversationId = 'standard';

        if (chatMode === 'time_capsule') {
            setter = setTimeCapsuleMessages;
            currentHistory = timeCapsuleMessages;
            conversationId = 'time_capsule';
        }
        else if (chatMode === 'origin_story') {
            setter = setOriginStoryMessages;
            currentHistory = originStoryMessages;
            conversationId = 'origin_story';
        }
        else {
            setter = setMessages;
            currentHistory = messages;
        }

        // 1. Add User Message
        const userMsg: Message = { id: Date.now().toString(), role: 'user', text, createdAt: Date.now() };
        setter(prev => [...prev, userMsg]);
        addMessage(user.uid, conversationId, userMsg).catch(console.error);

        // 2. Placeholder
        const tempId = (Date.now() + 1).toString();
        const modelMsgPlaceholder: Message = { id: tempId, role: 'model', text: "", createdAt: Date.now(), isThinking: true };
        setter(prev => [...prev, modelMsgPlaceholder]);

        setIsLoading(true);
        setKaiMood('thinking');

        try {
            // 3. UNIFIED HYBRID CALL (Cloud or Client)
            const updatedHistory = [...currentHistory, userMsg];

            const fullResponse = await sendMessageToKai(
                text,
                updatedHistory,
                kaiState.archetype,
                userFacts,
                summaries,
                ikigaiData // Pass buckets for context
            );

            // 4. Parse & Hidden Sidecar
            const { displayResponse, updates } = parseKaiResponse(fullResponse);

            // 5. Start Visual Output (Typing Effect)
            setKaiMood('talking');
            setter(prev => prev.map(msg => msg.id === tempId ? { ...msg, isThinking: false } : msg));

            simulateTyping(displayResponse, tempId, setter, () => {
                setIsLoading(false);
                setKaiMood('idle');

                const finalMsg: Message = { id: tempId, role: 'model', text: displayResponse, createdAt: Date.now() };
                addMessage(user.uid, conversationId, finalMsg).catch(console.error);

                // --- NEW: INJECT INSIGHT CARDS (With Alignment & Milestones) ---
                if (updates) {
                    console.log("🧩 [Kai Core] Insights detected:", updates);
                    onDashboardUpdate(updates); // Triggers parent state update

                    const insightsList: { type: 'passion' | 'mission' | 'profession' | 'vocation' | 'drains' | 'quest' | 'fact' | 'alignment' | 'milestone', items: string[] }[] = [];

                    // A. Calculate Status Shifts (Hypothetical Next State)
                    // We construct a temporary 'next' bucket state to see if stats changed
                    const nextBuckets = {
                        passion: [...ikigaiData.passion, ...(updates.passion || [])],
                        mission: [...ikigaiData.mission, ...(updates.mission || [])],
                        profession: [...ikigaiData.profession, ...(updates.profession || [])],
                        vocation: [...ikigaiData.vocation, ...(updates.vocation || [])],
                        drains: [...ikigaiData.drains, ...(updates.drains || [])]
                    };

                    const prevStats = calculateIkigaiStats(ikigaiData);
                    const nextStats = calculateIkigaiStats(nextBuckets);

                    // 1. Archetype Shift
                    if (prevStats.archetype !== nextStats.archetype && nextStats.archetype !== 'Calibrating') {
                        insightsList.push({ type: 'alignment', items: [`Archetype Detected: ${nextStats.archetype}`] });
                    }

                    // 2. Milestone Reached (Crossed 25, 50, 75, 100)
                    const thresholds = [25, 50, 75, 100];
                    const crossedThreshold = thresholds.find(t => prevStats.progress < t && nextStats.progress >= t);
                    if (crossedThreshold) {
                        insightsList.push({ type: 'milestone', items: [`Discovery Level: ${crossedThreshold}%`] });
                    }

                    // B. Standard Bucket Updates
                    if (updates.passion?.length) insightsList.push({ type: 'passion', items: updates.passion });
                    if (updates.mission?.length) insightsList.push({ type: 'mission', items: updates.mission });
                    if (updates.profession?.length) insightsList.push({ type: 'profession', items: updates.profession });
                    if (updates.vocation?.length) insightsList.push({ type: 'vocation', items: updates.vocation });
                    if (updates.drains?.length) insightsList.push({ type: 'drains', items: updates.drains });
                    if (updates.quest) insightsList.push({ type: 'quest', items: [updates.quest] });
                    if (updates.facts?.length) insightsList.push({ type: 'fact', items: updates.facts });

                    // Update the final message with valid insights
                    if (insightsList.length > 0) {
                        setter(prev => prev.map(msg =>
                            msg.id === tempId ? { ...msg, insights: insightsList } : msg
                        ));
                    }
                }
            });

        } catch (error) {
            console.error(error);
            const errorMsg: Message = { id: (Date.now() + 2).toString(), role: 'model', text: "Connection interrupted. Kai is offline." };
            setter(prev => prev.map(msg => msg.id === tempId ? { ...errorMsg, isThinking: false } : msg));
            setKaiMood('idle');
            setIsLoading(false);
        }
    }, [chatMode, messages, timeCapsuleMessages, originStoryMessages, kaiState.archetype, userFacts, summaries, setKaiMood, user, onDashboardUpdate, ikigaiData]); // Added ikigaiData dependency

    const injectSystemMessage = useCallback((text: string) => {
        const sysMsg: Message = {
            id: Date.now().toString(),
            role: 'model',
            text: `[SYSTEM MEMORY UPDATE] ${text}`,
            createdAt: Date.now()
        };
        setMessages(prev => [...prev, sysMsg]);
        if (user?.uid) {
            addMessage(user.uid, 'standard', sysMsg).catch(console.error);
        }
        startNewChat([...messagesRef.current, sysMsg], kaiState.archetype, summaries, userFacts, ikigaiData);
    }, [user, kaiState.archetype, summaries, userFacts, ikigaiData]);

    // NEW: Update Message State (Persist changes to action buttons)
    const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
        if (!user) return;
        
        let setter: React.Dispatch<React.SetStateAction<Message[]>>;
        let conversationId = 'standard';

        if (chatMode === 'time_capsule') {
            setter = setTimeCapsuleMessages;
            conversationId = 'time_capsule';
        } else if (chatMode === 'origin_story') {
            setter = setOriginStoryMessages;
            conversationId = 'origin_story';
        } else {
            setter = setMessages;
        }

        setter(prev => prev.map(msg => msg.id === messageId ? { ...msg, ...updates } : msg));
        
        // Persist change to DB
        updateChatMessage(user.uid, conversationId, messageId, updates).catch(console.error);
    }, [user, chatMode]);

    const handleModeChange = useCallback((mode: ChatMode) => {
        setChatMode(mode);
        if (mode === 'standard') switchToStandardChat(messages, kaiState.archetype, summaries, userFacts, ikigaiData);
        else if (mode === 'time_capsule') startTimeCapsuleChat(ikigaiData, timeCapsuleMessages);
        else if (mode === 'origin_story') startOriginStoryChat(ikigaiData, originStoryMessages);
    }, [ikigaiData, timeCapsuleMessages, originStoryMessages, messages, kaiState.archetype, summaries, userFacts]);

    const clearChats = () => {
        setMessages([]);
        setTimeCapsuleMessages([]);
        setOriginStoryMessages([]);
        startNewChat([], kaiState.archetype, [], [], ikigaiData);
    };

    return {
        chatMode,
        messages,
        timeCapsuleMessages,
        originStoryMessages,
        isLoading,
        setMessages,
        setTimeCapsuleMessages,
        setOriginStoryMessages,
        handleSendMessage,
        handleModeChange,
        injectSystemMessage,
        clearChats,
        updateMessage // Export function
    };
};